import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import TicketForm from "@/components/public/AssistantTicketForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SKEY = "rs_assistant_session";
const TAG = /\[\[T?I?C?K?E?T?\]?\]?$|\[\[TICKET\]\]/g;
const GREETING = { role: "assistant", content: "Hi, I'm Sifty, the RecruitSift assistant. Ask me how AI phone, video and coding interviews work, or how to get started." };
const PROMPTS = ["What does RecruitSift do?", "How does AI phone screening work?", "Is it fair to candidates?", "I'd like a demo"];

async function streamChat(body, onEvent) {
  const res = await fetch(`${API}/assistant/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Something went wrong");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    parts.forEach((p) => p.startsWith("data: ") && onEvent(JSON.parse(p.slice(6))));
  }
}

function Bubble({ m, i }) {
  const me = m.role === "user";
  return (
    <div className={`flex ${me ? "justify-end" : "justify-start"}`} data-testid={`assistant-msg-${i}`}>
      <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${me ? "bg-slate-900 text-white rounded-br-md" : "bg-slate-100 text-slate-800 rounded-bl-md"}`}>
        {m.content || <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      </div>
    </div>
  );
}

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([GREETING]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [ticket, setTicket] = useState(false);
  const sid = useRef(localStorage.getItem(SKEY));
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, ticket]);
  useEffect(() => {
    if (!sid.current) return;
    fetch(`${API}/assistant/session/${sid.current}`).then((r) => r.json())
      .then((d) => d.messages?.length && setMsgs([GREETING, ...d.messages])).catch(() => {});
  }, []);

  const patchLast = (fn) => setMsgs((m) => [...m.slice(0, -1), fn(m[m.length - 1])]);
  const send = async (raw) => {
    const t = (raw ?? text).trim();
    if (!t || busy) return;
    setText(""); setBusy(true); setTicket(false);
    setMsgs((m) => [...m, { role: "user", content: t }, { role: "assistant", content: "" }]);
    try {
      await streamChat({ session_id: sid.current, message: t }, (ev) => {
        if (ev.session_id) { sid.current = ev.session_id; localStorage.setItem(SKEY, ev.session_id); }
        if (ev.delta) patchLast((x) => ({ ...x, content: (x.raw || "") + ev.delta, raw: (x.raw || "") + ev.delta }));
        if (ev.done) { patchLast((x) => ({ ...x, content: ev.text })); if (ev.escalate) setTicket(true); }
      });
    } catch (e) {
      patchLast((x) => ({ ...x, content: `${e.message}` })); setTicket(true);
    } finally { setBusy(false); }
  };
  const shown = msgs.map((m) => ({ ...m, content: (m.content || "").replace(TAG, "").trimEnd() }));

  return (
    <>
      {open && (
        <div className="fixed z-50 bottom-0 right-0 sm:bottom-24 sm:right-6 w-full sm:w-[380px] h-[100dvh] sm:h-[560px] bg-white sm:rounded-2xl border border-slate-200 shadow-2xl flex flex-col overflow-hidden" data-testid="assistant-panel">
          <div className="px-4 py-3 bg-[#0f1b2e] text-white flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
            <div className="flex-1"><div className="text-sm font-semibold">Sifty · RecruitSift Assistant</div><div className="text-[11px] text-slate-300">AI answers instantly · our team follows up</div></div>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="assistant-close" aria-label="Close chat"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="assistant-messages">
            {shown.map((m, i) => <Bubble key={i} m={m} i={i} />)}
            {msgs.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {PROMPTS.map((p, i) => <button key={p} onClick={() => send(p)} data-testid={`assistant-prompt-${i}`} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-700 hover:border-blue-400 hover:text-blue-600 transition-colors">{p}</button>)}
              </div>
            )}
            {ticket && <TicketForm sessionId={sid.current} onDone={(n) => { setTicket(false); setMsgs((m) => [...m, { role: "assistant", content: `Thanks! Your request #${n} has reached our team. We'll reply by email soon.` }]); }} />}
            <div ref={end} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-slate-100 flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1500} placeholder="Ask about RecruitSift…" data-testid="assistant-input"
              className="flex-1 px-3.5 py-2.5 rounded-xl border border-slate-200 text-base sm:text-sm focus:outline-none focus:border-blue-500" />
            <button disabled={busy || !text.trim()} data-testid="assistant-send" className="px-3 rounded-xl bg-blue-500 hover:bg-blue-600 disabled:opacity-40 text-white transition-colors" aria-label="Send"><Send className="w-4 h-4" /></button>
          </form>
          <button onClick={() => setTicket(true)} className="pb-2 text-[11px] text-slate-500 hover:text-blue-600" data-testid="assistant-talk-to-team">Prefer a human? Contact our team</button>
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} data-testid="assistant-launcher" aria-label="Open chat assistant"
        className={`fixed z-50 bottom-5 right-5 sm:bottom-6 sm:right-6 w-14 h-14 rounded-full bg-blue-500 hover:bg-blue-600 text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105 ${open ? "hidden sm:flex" : ""}`}>
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </>
  );
}
